import React, {memo} from 'react';
import {
  Modal,
  StyleSheet,
  View,
  Dimensions,
  ScrollView,
  Platform,
} from 'react-native';
import moment from 'moment';
import Styles from '../Styles';
import Localized from 'src/constants/AppStrings';
import AVText from './AVText';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';
import CancelIcon from 'src/components/img/svg/CancelIcon';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import {getToastDescriber} from 'src/components/elements/toastDescriptor/DescroiptorType';
import Util from 'src/Util';
import AccountStore from 'src/stores/AccountStore';
import {MoreWaysToEarnPolicyModel} from 'src/types/RewardsModel';
import KeyboardAvoidingView from 'src/components/elements/365KeyboardAvoidingView';
import RewardsAppSurvey from 'src/components/elements/RewardsAppSurvey';
import {RewardPolicyEnum} from 'src/constants/RewardPolicyEnum';

export type RewardsModalProps = {
  title: string;
  isRedirectToHome?: boolean;
  testIDTitle?: string;
  testIDButton?: string;
  testIDMsg?: string;
  minimumSpendAmount?: number;
  policyItem?: MoreWaysToEarnPolicyModel;
  visible?: boolean;
  onStartShoppingPress?: (answers?: {quest1: string; quest2?: string}) => void;
  onRequestClose: () => void;
  returnWithinDays?: number;
  endDate?: string;
  requiredPurchaseCount?: number;
  currentPurchaseCount?: number;
  policyType?: string;
};

const screenWidth = Dimensions.get('window').width;
const modalWidth =
  Platform.OS === 'web' ? screenWidth * 0.3 : screenWidth * 0.9;

const toSafeString = (input: string | Array<string | number>): string =>
  Array.isArray(input) ? input.join('') : input;

export const RewardsModal = memo(
  ({
    title,
    testIDTitle,
    testIDButton,
    testIDMsg,
    minimumSpendAmount,
    visible,
    onStartShoppingPress,
    onRequestClose,
    policyItem,
    returnWithinDays,
    endDate,
    requiredPurchaseCount,
    currentPurchaseCount,
    policyType,
  }: RewardsModalProps) => {
    const [firstQuestionAns, setFirstQuestion] = React.useState('');
    const [secondQuestionAns, setSecondQuestionAns] = React.useState('');
    const isNoExpiry = endDate === '9999-01-01';

    const questions =
      policyItem?.policy?.surveyQuestions?.filter((q) => q?.trim()) || [];
    const hasSecondQuestion = questions.length > 1;

    const getFormattedAmount = () => {
      if (minimumSpendAmount === undefined) return '';
      let formatted = Util.formatCurrency(
        policyItem,
        minimumSpendAmount,
        AccountStore.getCurrency(),
        {minimumFractionDigits: 0, maximumFractionDigits: 2},
      );
      return formatted.endsWith('.00') ? formatted.slice(0, -3) : formatted;
    };

    const renderDescription = () => {
      const formattedAmount = getFormattedAmount();
      const formattedDate = endDate ? moment(endDate).format('MM/DD/YYYY') : '';
      const count = requiredPurchaseCount ?? 0;
      const currentCount = currentPurchaseCount ?? 0;

      if (minimumSpendAmount) {
        return {
          header: Localized.Labels.formatString(
            Localized.Labels.spend_header_text,
            formattedAmount,
          ),
          descriptionText: Localized.Labels.formatString(
            Localized.Labels.spend_desc_text,
            formattedAmount,
          ),
          descriptionSubText: Localized.Labels.spend_desc_sub_text,
        };
      }

      if (returnWithinDays) {
        return {
          header: Localized.Labels.formatString(
            Localized.Labels.rewards_come_back_soon_header,
            returnWithinDays,
          ),
          descriptionText: Localized.Labels.formatString(
            Localized.Labels.rewards_come_back_soon_Desc,
            returnWithinDays,
          ),
          descriptionSubText: Localized.Labels.spend_desc_sub_text,
        };
      }

      if (requiredPurchaseCount || currentPurchaseCount) {
        const labelKey = isNoExpiry
          ? Localized.Labels.rewards_more_purchase_no_expire_date
          : Localized.Labels.rewards_more_purchase_desc;

        const params = isNoExpiry ? [count] : [count, formattedDate];

        return {
          header: Localized.Labels.rewards_more_purchase_header,
          descriptionText: Localized.Labels.formatString(labelKey, ...params),
          descriptionSubText: Localized.Labels.rewards_more_purchase_sub_text,
          purchaseCount: Localized.Labels.formatString(
            Localized.Labels.rewards_more_purchase_count,
            currentCount,
            count,
          ),
          expireDate: isNoExpiry
            ? ''
            : Localized.Labels.formatString(
                Localized.Labels.rewards_more_purchase_exp_date,
                formattedDate,
              ),
        };
      }

      return {header: '', descriptionText: '', descriptionSubText: ''};
    };

    const {
      header,
      descriptionText,
      descriptionSubText,
      purchaseCount,
      expireDate,
    } = renderDescription();

    const isAppSurvey = policyType === RewardPolicyEnum.APP_SURVEY;

    const isSubmitEnabled = React.useMemo(() => {
      if (!isAppSurvey) return true;

      if (questions.length === 1) {
        return firstQuestionAns.trim().length > 0;
      }

      if (questions.length === 2) {
        return (
          firstQuestionAns.trim().length > 0 &&
          secondQuestionAns.trim().length > 0
        );
      }

      return false;
    }, [isAppSurvey, questions.length, firstQuestionAns, secondQuestionAns]);

    const buttonLabel = isAppSurvey
      ? Localized.Buttons.rewards_modal_submit
      : Localized.Buttons.start_shopping;
    const handleAppSurveyOrStartShopping = () => {
      const isAppSurveyNow = policyType === RewardPolicyEnum.APP_SURVEY;

      const answers = {
        quest1: firstQuestionAns,
        quest2: secondQuestionAns,
      };

      if (isAppSurveyNow) {
        onStartShoppingPress?.(answers);
      } else {
        onStartShoppingPress?.();
      }
      onRequestClose?.();
    };

    return (
      <Modal visible={visible} onRequestClose={onRequestClose} transparent>

          <View style={styles.successModalView}>
            <View style={styles.successModalContainer}>
              <AVTouchableOpacity
                aria-label={`X icon, Double tap to ${Localized.Buttons.cancel} rewards`}
                role="button"
                style={styles.closeModalTitleContainer}
                onPress={onRequestClose}
                accessible
                accessibilityLabel={Localized.Buttons.cancel}
                accessibilityRole="button"
                hitSlop={Styles.HitSlop_10}
              >
                <CancelIcon />
              </AVTouchableOpacity>

              <ScrollView
                contentContainerStyle={styles.scrollContainer}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
              >
                {policyType === RewardPolicyEnum.APP_SURVEY ? (
                  <RewardsAppSurvey
                    questions={questions}
                    firstAnswer={firstQuestionAns}
                    secondAnswer={secondQuestionAns}
                    onChangeFirstAnswer={setFirstQuestion}
                    onChangeSecondAnswer={setSecondQuestionAns}
                  />
                ) : (
                  <View style={styles.successModalViewContent}>
                    <AVText
                      aria-label={`${Localized.Success.success} label, text`}
                      accessibilityLabel={`${Localized.Success.success} label`}
                      style={styles.header}
                      accessible
                      maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
                      testID={testIDTitle}
                      nativeID={testIDTitle}
                    >
                      {toSafeString(header)}
                    </AVText>

                    <AVText
                      accessible
                      accessibilityLabel={`${title} label`}
                      aria-label={`${title} label, text`}
                      style={[styles.title]}
                      maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
                      testID={testIDMsg}
                      nativeID={testIDMsg}
                    >
                      {toSafeString(descriptionText)}
                    </AVText>

                    <AVText
                      accessible
                      accessibilityLabel={`${title} label`}
                      aria-label={`${title} label, text`}
                      style={[styles.title, {fontStyle: 'italic'}]}
                      maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
                      testID={testIDMsg}
                      nativeID={testIDMsg}
                    >
                      {toSafeString(descriptionSubText)}
                    </AVText>

                    {policyType === RewardPolicyEnum.PURCHASE_STREAK && (
                      <View style={styles.purchaseView}>
                        <AVText
                          style={styles.purchaseCountText}
                          maxFontSizeMultiplier={
                            Styles.FontSizeMultiplier.maxfm7
                          }
                          testID={testIDMsg}
                        >
                          {toSafeString(purchaseCount)}
                        </AVText>
                        {!isNoExpiry && (
                          <AVText
                            style={styles.expDateText}
                            maxFontSizeMultiplier={
                              Styles.FontSizeMultiplier.maxfm7
                            }
                            testID={testIDMsg}
                          >
                            {toSafeString(expireDate)}
                          </AVText>
                        )}
                      </View>
                    )}
                  </View>
                )}

                <RoundedAppButton
                  buttonType={RoundedAppButtonType.Solid}
                  buttonViewStyle={[
                    styles.btnViewStyle,
                    isAppSurvey && styles.appSurveyViewStyle,
                  ]}
                  buttonContainerStyle={styles.btnContainer}
                  accessibilityLabelValue={buttonLabel}
                  titleText={buttonLabel}
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm1}
                  textStyle={styles.okayButtonText}
                  testID={testIDButton}
                  onPress={handleAppSurveyOrStartShopping}
                  isDisabled={!isSubmitEnabled}
                  inActiveButtonViewStyle={[
                    {
                      paddingHorizontal: Styles.Spacing.m2 + Styles.Spacing.m3,
                      backgroundColor: Styles.Colors.PayNew.darkHuesBase04,
                    },
                  ]}
                  inActiveTextStyle={{
                    fontSize: Styles.Fonts.f1,
                  }}
                />
              </ScrollView>
            </View>
          </View>
     
      </Modal>
    );
  },
);

const styles = StyleSheet.create({
  successModalView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  successModalContainer: {
    width: modalWidth,
    maxHeight: '71%',
    backgroundColor: Styles.white,
    borderRadius: Styles.Spacing.m3,
    paddingHorizontal: 25,
    paddingTop: Styles.Heights.h1 * 2,
    alignSelf: 'center',
  },
  scrollContainer: {
    paddingBottom: Styles.Spacing.m4,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: Styles.Spacing.m2,
  },
  successModalViewContent: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeModalTitleContainer: {
    position: 'absolute',
    top: Styles.Heights.h1,
    right: Styles.Heights.h1,
    zIndex: 1,
  },
  okayButtonText: {
    color: Styles.white,
    fontSize: Styles.Fonts.f1,
    fontWeight: '700',
    textAlign: 'center',
    fontFamily: getToastDescriber().snackBarFontFamily(),
    textTransform: 'none',
  },
  header: {
    color: Styles.black,
    fontSize: Styles.Fonts.f9 - 1,
    fontWeight: '700',
    fontFamily: getToastDescriber().snackBarFontFamily(),
    marginTop: Styles.Spacing.m2,
  },
  title: {
    color: Styles.darkColor,
    fontSize: Styles.Fonts.f1,
    fontWeight: '400',
    fontFamily: getToastDescriber().snackBarFontFamily(),
    marginTop: Styles.Spacing.m3,
    textAlign: 'center',
  },
  btnViewStyle: {
    height: 42,
    paddingHorizontal: Styles.Spacing.m3,
  },
  appSurveyViewStyle: {
    paddingHorizontal: Styles.Spacing.m2 + Styles.Spacing.m3,
  },
  btnContainer: {
    alignSelf: 'center',
    marginTop: Styles.Spacing.m4,
  },
  expDateText: {
    color: Styles.darkColor,
    fontSize: Styles.Fonts.f8,
    fontWeight: '400',
    fontFamily: getToastDescriber().snackBarFontFamily(),
    marginTop: Styles.Spacing.m2,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  purchaseCountText: {
    color: Styles.darkColor,
    fontSize: Styles.Fonts.f8,
    fontWeight: '700',
    fontFamily: getToastDescriber().snackBarFontFamily(),
    marginTop: Styles.Spacing.m3,
    textAlign: 'center',
  },
  purchaseView: {
    top: Styles.Spacing.m2,
  },
  msgContentStyle: {
    paddingTop: Styles.Spacing.m2,
    paddingBottom: Styles.Spacing.m3,
    top: Styles.Spacing.m1,
    height: 130,
  },

  msgLableViewTextStyle: {
    fontSize: Styles.Fonts.f8,
    fontWeight: '400',
    color: Styles.Colors.PayNew.darkHuesBase04,
    marginBottom: Styles.Spacing.m1,
  },

  msgLabelOutlineStyle: {
    borderColor: Styles.inActiveDotColor,
    borderRadius: Styles.Spacing.m2,
    borderWidth: 1,
    elevation: Platform.OS === 'android' ? 2 : 0,
    backgroundColor: Styles.white,
  },

  payDescriptionCountLabel: {
    alignSelf: 'flex-end',
    marginTop: Styles.Spacing.m1,
    fontSize: Styles.Fonts.f8,
    color: Styles.Colors.PayLegacy.grayScaleBase03,
  },
  inputLabelText: {
    fontSize: Styles.Fonts.f1,
    fontWeight: '500',
    color: Styles.darkColor,
    marginTop: Styles.Spacing.m3,
    textAlign: 'center',
  },
  appSurveyView: {
    width: '100%',
  },
  textContainerStyle: {
    width: '100%',
    marginTop: Styles.Spacing.m3,
    minHeight: 130,
    maxHeight: 150,
  },
});
